import { acaoSobDemanda, telaSobDemanda } from '../../nucleo/paginas/TelaSobDemanda';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../nucleo/api';
import { CartaoIndicador } from '../../nucleo/ui/Estados';
import type { ModuloWeb } from '../../nucleo/modulos';
import { dicionario } from './i18n';
import { useFormatos } from './Comum';

type IndicadoresAgenda = {
  hoje: number;
  confirmados: number;
  a_confirmar: number;
  externos: number;
  total: number;
  proximos: { id: string; origem: 'agendamento' | 'compromisso'; titulo: string; inicio: string; recurso: string | null }[];
};

/** Cartão do Início que leva à agenda do dia; no erro fica sem link (o botão "Tentar de novo" não vai dentro de um link). */
function ComLink({ erro, children }: { erro: boolean; children: ReactNode }) {
  if (erro) return <>{children}</>;
  return (
    <Link to="/agenda/dia" className="block min-w-0 rounded-xl hover:*:border-acento">
      {children}
    </Link>
  );
}

/**
 * F10: agenda de hoje no Início. SPEC-BASE-R6-01: o cartão do dia soma os agendamentos e os compromissos abertos de
 * hoje (vindos de outros módulos), e o próximo compromisso considera os dois. ROB-01: CartaoIndicador do núcleo
 * (CONTRATOS 7.13) mostra o traço e "Tentar de novo" quando a API falha, em vez de "..." para sempre.
 */
function Indicadores() {
  const { t } = useTranslation('agenda');
  const f = useFormatos();
  const q = useQuery({
    queryKey: ['agenda', 'indicadores'],
    queryFn: () => api<IndicadoresAgenda>('/api/v1/agenda/indicadores'),
  });
  const consultas = [q];
  const prox = q.data?.proximos[0];
  return (
    <>
      <ComLink erro={q.isError}>
        <CartaoIndicador rotulo={t('indicadores.hoje')} valor={q.data ? q.data.total : null} consultas={consultas} />
      </ComLink>
      <ComLink erro={q.isError}>
        <CartaoIndicador
          rotulo={t('indicadores.aConfirmar')}
          valor={q.data ? <span className={q.data.a_confirmar ? 'text-laranja' : ''}>{q.data.a_confirmar}</span> : null}
          consultas={consultas}
        />
      </ComLink>
      <ComLink erro={q.isError}>
        <CartaoIndicador
          rotulo={t('indicadores.proximo')}
          valor={q.data ? (prox ? f.hora(prox.inicio) : <span className="text-sm font-normal text-tinta-3">{t('indicadores.nenhum')}</span>) : null}
          consultas={consultas}
          detalhe={prox && <p className="mt-1 truncate text-xs text-tinta-3" title={prox.recurso ? `${prox.titulo} · ${prox.recurso}` : prox.titulo}>{prox.recurso ? `${prox.titulo} · ${prox.recurso}` : prox.titulo}</p>}
        />
      </ComLink>
    </>
  );
}

const carregar0 = () => import('./Comum');
const carregar1 = () => import('./Visoes');
const carregar2 = () => import('./Recursos');
const carregar3 = () => import('./Servicos');
const carregar4 = () => import('./PainelParceiro');

const AcaoNovo = acaoSobDemanda(carregar0, 'AcaoNovo');
const VisaoAgenda = telaSobDemanda(carregar1, 'VisaoAgenda');
const Recursos = telaSobDemanda(carregar2, 'Recursos');
const Servicos = telaSobDemanda(carregar3, 'Servicos');
const PainelAgendamentosParceiro = telaSobDemanda(carregar4, 'PainelAgendamentosParceiro');

export const modulo: ModuloWeb = {
  slug: 'agenda',
  menu: 'agenda',
  dicionario,
  abas: [
    { rotulo: 'abas.semana', caminho: 'semana', elemento: <VisaoAgenda key="semana" visao="semana" />, Acao: () => <AcaoNovo rotulo="acoes.novoAgendamento" />, ordem: 1 },
    { rotulo: 'abas.dia', caminho: 'dia', elemento: <VisaoAgenda key="dia" visao="dia" />, Acao: () => <AcaoNovo rotulo="acoes.novoAgendamento" />, ordem: 2 },
    { rotulo: 'abas.mes', caminho: 'mes', elemento: <VisaoAgenda key="mes" visao="mes" />, Acao: () => <AcaoNovo rotulo="acoes.novoAgendamento" />, ordem: 3 },
    { rotulo: 'abas.recursos', caminho: 'profissionais-e-recursos', elemento: <Recursos />, Acao: () => <AcaoNovo rotulo="acoes.novoRecurso" />, ordem: 4 },
    { rotulo: 'abas.servicos', caminho: 'servicos', elemento: <Servicos />, Acao: () => <AcaoNovo rotulo="acoes.novoServico" />, ordem: 5 },
  ],
  Indicadores,
  // M1.7 (SPEC1-03): agendamentos do parceiro na ficha do Cadastros.
  paineis: [{ entidade: 'cadastros.parceiro', titulo: 'painel.titulo', Componente: PainelAgendamentosParceiro, ordem: 30 }],
};
